import { useTranslation } from "react-i18next";
import { Check } from "lucide-react";
import { cn } from "../../../../lib/utils.js";
import type { TemplateCreationStep } from "../template-creation-wizard-state.js";

const STEPS: readonly { key: TemplateCreationStep; labelKey: string }[] = [
  { key: "DEFINITION", labelKey: "whatsapp.templates.manage.wizard.steps.definition" },
  { key: "EDIT", labelKey: "whatsapp.templates.manage.wizard.steps.edit" },
  { key: "REVIEW", labelKey: "whatsapp.templates.manage.wizard.steps.review" },
] as const;

const STEP_ORDER: Record<TemplateCreationStep, number> = {
  DEFINITION: 0,
  EDIT: 1,
  REVIEW: 2,
};

interface WizardStepIndicatorProps {
  currentStep: TemplateCreationStep;
}

export function WizardStepIndicator({
  currentStep,
}: WizardStepIndicatorProps): React.JSX.Element {
  const { t } = useTranslation();
  const currentIndex = STEP_ORDER[currentStep];

  return (
    <nav aria-label={t("whatsapp.templates.manage.wizard.steps.definition")}>
      <ol className="flex items-center justify-center gap-0">
        {STEPS.map((step, index) => {
          const isCompleted = index < currentIndex;
          const isCurrent = index === currentIndex;
          const isLast = index === STEPS.length - 1;

          return (
            <li
              key={step.key}
              className="flex items-center"
              aria-current={isCurrent ? "step" : undefined}
            >
              <div className="flex items-center gap-2">
                <span
                  className={cn(
                    "flex size-7 shrink-0 items-center justify-center rounded-full text-xs font-semibold transition-colors",
                    isCompleted && "bg-accent text-white",
                    isCurrent && "bg-accent text-white",
                    !isCompleted && !isCurrent && "bg-surface-muted text-ink-muted",
                  )}
                  aria-hidden="true"
                >
                  {isCompleted ? (
                    <Check size={14} strokeWidth={2.5} />
                  ) : (
                    index + 1
                  )}
                </span>
                <span
                  className={cn(
                    "hidden text-sm font-medium sm:inline",
                    isCurrent ? "text-ink" : "text-ink-muted",
                  )}
                >
                  {t(step.labelKey)}
                </span>
              </div>
              {!isLast && (
                <div
                  className={cn(
                    "mx-3 h-px w-8 sm:w-12",
                    index < currentIndex ? "bg-accent" : "bg-line",
                  )}
                  aria-hidden="true"
                />
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
